import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { AE_LABEL, AE_SELECT } from '@/components/assessment-engine/layout/aeTheme';
import type { AeQuestionDisplayConfig } from '@/types/assessmentEngine';
import { SCALE_DISPLAY_STYLES } from './scaleUtils';
import type { QuestionDraft } from './questionDraftTypes';
import type { AeTemplateScalePreset } from './templateConfigTypes';
import {
  applyScalePreset,
  questionTypeSelectionValue,
} from './scalePresetLink';

export { applyScalePreset, applyMatrixScalePreset, questionTypeSelectionValue } from './scalePresetLink';

interface QuestionBuilderSettingsProps {
  draft: QuestionDraft;
  onChange: (patch: Partial<QuestionDraft>) => void;
  scalePresets?: AeTemplateScalePreset[];
  readOnly?: boolean;
}

/**
 * Survey-design question settings: type (incl. named Setup scales), required,
 * reverse scored, and display choice. Scale length / min / max / labels / N/A
 * are defined only on the Setup tab scale presets.
 */
export const QuestionBuilderSettings: React.FC<QuestionBuilderSettingsProps> = ({
  draft,
  onChange,
  scalePresets = [],
  readOnly = false,
}) => {
  const { t } = useLanguage();
  const display = draft.display_config;
  const usesScale =
    draft.question_type === 'scale' ||
    draft.question_type === 'nps' ||
    draft.question_type === 'matrix';

  const updateDisplay = (patch: Partial<AeQuestionDisplayConfig>) => {
    onChange({ display_config: { ...display, ...patch } });
  };

  return (
    <div className="pb-3 mb-3 border-b border-dashed border-[var(--color-border)] space-y-3 text-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className={AE_LABEL}>{t('assessmentEngine.builder.questionType')}</label>
          <select
            className={AE_SELECT}
            value={questionTypeSelectionValue(draft, scalePresets)}
            disabled={readOnly}
            onChange={(e) => {
              const selection = e.target.value;
              if (selection.startsWith('scale-preset:')) {
                const preset = scalePresets.find(
                  (candidate) => candidate.id === selection.slice('scale-preset:'.length)
                );
                if (preset) onChange(applyScalePreset(preset));
                return;
              }
              // Leaving a named shared scale — drop the durable link.
              const restDisplay = { ...display };
              delete restDisplay.scalePresetId;
              onChange({
                question_type: selection as QuestionDraft['question_type'],
                display_config: restDisplay,
              });
            }}
          >
            {(
              [
                'short_text',
                'long_text',
                'single_choice',
                'multi_choice',
                'scale',
                'nps',
                'matrix',
              ] as const
            ).map((type) => (
              <option key={type} value={type}>
                {t(`assessmentEngine.questionType.${type}`)}
              </option>
            ))}
            {scalePresets.length > 0 && (
              <optgroup label={t('assessmentEngine.builder.scalePresets')}>
                {scalePresets.map((preset) => (
                  <option key={preset.id} value={`scale-preset:${preset.id}`}>
                    {preset.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        {(draft.question_type === 'single_choice' || draft.question_type === 'multi_choice') && (
          <div>
            <label className={AE_LABEL}>{t('assessmentEngine.builder.choiceDisplay')}</label>
            <select
              className={AE_SELECT}
              value={display.choiceDisplay ?? 'radio'}
              disabled={readOnly}
              onChange={(e) =>
                updateDisplay({
                  choiceDisplay: e.target.value as AeQuestionDisplayConfig['choiceDisplay'],
                })
              }
            >
              {(draft.question_type === 'single_choice'
                ? ['radio', 'dropdown', 'checkbox']
                : ['checkbox', 'boxes']
              ).map((d) => (
                <option key={d} value={d}>
                  {t(`assessmentEngine.builder.display.${d}`)}
                </option>
              ))}
            </select>
          </div>
        )}

        {usesScale && (
          <div>
            <label className={AE_LABEL}>{t('assessmentEngine.builder.scaleDisplayStyle')}</label>
            <select
              className={AE_SELECT}
              value={display.scaleDisplayStyle ?? 'numbers'}
              disabled={readOnly}
              onChange={(e) =>
                updateDisplay({
                  scaleDisplayStyle: e.target.value as AeQuestionDisplayConfig['scaleDisplayStyle'],
                })
              }
            >
              {SCALE_DISPLAY_STYLES.map((s) => (
                <option key={s} value={s}>
                  {t(`assessmentEngine.builder.scaleStyle.${s}`)}
                </option>
              ))}
            </select>
          </div>
        )}

        <label className="flex items-center gap-2 pt-6">
          <input
            type="checkbox"
            checked={draft.is_required}
            disabled={readOnly}
            onChange={(e) => onChange({ is_required: e.target.checked })}
            className="accent-[var(--color-accent-bg)]"
          />
          {t('assessmentEngine.builder.required')}
        </label>

        {usesScale && (
          <label className="flex items-center gap-2 pt-6">
            <input
              type="checkbox"
              checked={display.reverseScored ?? false}
              disabled={readOnly}
              onChange={(e) => updateDisplay({ reverseScored: e.target.checked })}
              className="accent-[var(--color-accent-bg)]"
            />
            {t('assessmentEngine.builder.reverseScored')}
          </label>
        )}
      </div>
    </div>
  );
};
